import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';

type GemmaState = 'idle' | 'recording' | 'recorded' | 'analyzing' | 'complete' | 'failed';

interface GemmaHealth {
  status?: string;
  modelId?: string;
  modelLoaded?: boolean;
  modelLoading?: boolean;
  modelError?: string;
  transcriptionWorkerUrl?: string;
}

type GemmaMetric = string | { label?: string; score?: number; evidence?: string };

interface GemmaExtraction {
  transcript?: string;
  language?: string;
  sentiment?: GemmaMetric;
  intent?: GemmaMetric;
  urgency?: GemmaMetric;
  personName?: string | null;
  phone?: string | null;
  cnic?: string | null;
  location?: string | null;
  department?: string | null;
  entities?: Record<string, unknown>;
  complaint?: Record<string, unknown>;
  summary?: string;
  action?: string;
  recommendedAction?: string;
  needsHumanReview?: boolean;
  rawModelText?: string;
  error?: string;
}

interface NormalizedGemmaExtraction extends Omit<GemmaExtraction, 'sentiment' | 'intent' | 'urgency'> {
  sentiment?: { label?: string; score?: number; evidence?: string };
  intent?: { label?: string; score?: number; evidence?: string };
  urgency?: { label?: string; score?: number; evidence?: string };
}

@Component({
  selector: 'app-rnd-gemma-voice-function',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './rnd-gemma-voice-function.component.html',
  styleUrls: ['./rnd-gemma-voice-function.component.scss'],
})
export class RndGemmaVoiceFunctionComponent implements OnInit, OnDestroy {
  state: GemmaState = 'idle';
  elapsedSeconds = 0;
  audioUrl = '';
  mimeType = '';
  fileSize = 0;
  selectedFileName = '';
  language = 'auto';
  extractionPrompt = [
    'Extract sentiment, intent, urgency, caller entities, complaint category, complaint details, and recommended next action.',
    'Return only structured JSON suitable for a contact center supervisor.',
  ].join(' ');
  health: GemmaHealth | null = null;
  result: NormalizedGemmaExtraction | null = null;
  rawResponse = '';
  showRaw = false;
  errorMessage = '';

  private mediaStream: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private audioBlob: Blob | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private startedAt = 0;

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    this.loadHealth();
  }

  ngOnDestroy(): void {
    this.stopPoll();
    this.clear(false);
  }

  get statusLabel(): string {
    switch (this.state) {
      case 'recording': return 'Recording';
      case 'recorded': return 'Audio ready';
      case 'analyzing': return 'Gemma analysis running';
      case 'complete': return 'Analysis complete';
      case 'failed': return 'Action failed';
      default: return 'Ready';
    }
  }

  get canRecord(): boolean {
    return this.state !== 'recording' && this.state !== 'analyzing';
  }

  get canAnalyze(): boolean {
    return !!this.audioBlob && this.state !== 'recording' && this.state !== 'analyzing';
  }

  get formattedDuration(): string {
    const minutes = Math.floor(this.elapsedSeconds / 60).toString().padStart(2, '0');
    const seconds = (this.elapsedSeconds % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  }

  get fileSizeLabel(): string {
    if (!this.fileSize) return '-';
    if (this.fileSize < 1024 * 1024) return `${Math.round(this.fileSize / 1024)} KB`;
    return `${(this.fileSize / (1024 * 1024)).toFixed(2)} MB`;
  }

  async loadHealth(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.get<any>(`${this.rndApiBase}/gemma-voice/health`)
      );
      this.health = response?.data || response;
      if (this.health?.modelLoaded) {
        this.stopPoll();
      } else if (this.health?.modelLoading) {
        this.startPoll();
      }
    } catch (error) {
      this.health = { status: 'UNAVAILABLE' };
    }
  }

  async downloadModel(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.post<any>(`${this.rndApiBase}/gemma-voice/download`, {})
      );
      const payload = response?.data || response;
      if (payload?.status === 'ALREADY_LOADED') {
        await this.loadHealth();
        return;
      }
      this.health = { ...(this.health ?? {}), modelLoading: true, status: 'LOADING' };
      this.startPoll();
    } catch (error) {
      this.errorMessage = 'Failed to trigger model download.';
    }
  }

  private startPoll(): void {
    if (this.pollTimer) return;
    this.pollTimer = setInterval(() => this.loadHealth(), 5000);
  }

  private stopPoll(): void {
    if (!this.pollTimer) return;
    clearInterval(this.pollTimer);
    this.pollTimer = null;
  }

  async startRecording(): Promise<void> {
    if (!this.canRecord) return;
    this.clear(false);
    this.errorMessage = '';

    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      });

      this.mimeType = this.pickMimeType();
      this.recorder = new MediaRecorder(this.mediaStream, this.mimeType ? { mimeType: this.mimeType } : undefined);
      this.chunks = [];
      this.startedAt = Date.now();
      this.elapsedSeconds = 0;
      this.state = 'recording';

      this.recorder.addEventListener('dataavailable', event => {
        if (event.data?.size) this.chunks.push(event.data);
      });
      this.recorder.addEventListener('stop', () => this.finalizeRecording());
      this.recorder.start(1000);
      this.timer = setInterval(() => {
        this.elapsedSeconds = Math.floor((Date.now() - this.startedAt) / 1000);
      }, 250);
    } catch (error) {
      this.state = 'failed';
      this.errorMessage = error instanceof Error ? error.message : 'Microphone access failed.';
      this.stopTracks();
    }
  }

  stopRecording(): void {
    if (!this.recorder || this.recorder.state === 'inactive') return;
    this.recorder.stop();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.clear(false);
    this.audioBlob = file;
    this.fileSize = file.size;
    this.mimeType = file.type || 'audio/*';
    this.selectedFileName = file.name;
    this.audioUrl = URL.createObjectURL(file);
    this.state = 'recorded';
  }

  async analyze(): Promise<void> {
    if (!this.audioBlob) return;
    this.state = 'analyzing';
    this.errorMessage = '';
    this.result = null;
    this.rawResponse = '';

    const form = new FormData();
    form.append('file', this.audioBlob, this.selectedFileName || `gemma-voice-${Date.now()}.${this.fileExtension()}`);
    form.append('language', this.language);
    form.append('extractionPrompt', this.extractionPrompt);

    try {
      const response = await firstValueFrom(
        this.http.post<any>(`${this.rndApiBase}/gemma-voice/extract`, form)
      );
      const payload = response?.data || response;
      this.rawResponse = JSON.stringify(payload, null, 2);
      this.result = this.normalizeExtraction(payload?.extraction || payload);
      this.state = 'complete';
      await this.loadHealth();
    } catch (error: any) {
      this.state = 'failed';
      this.errorMessage = error?.error?.message || error?.message || 'Gemma voice analysis failed.';
      if (error?.error) {
        this.rawResponse = JSON.stringify(error.error, null, 2);
      }
    }
  }

  clear(resetState = true): void {
    if (this.recorder && this.recorder.state !== 'inactive') {
      try { this.recorder.stop(); } catch {}
    }
    this.stopTimer();
    this.stopTracks();
    this.recorder = null;
    this.chunks = [];
    this.audioBlob = null;
    this.fileSize = 0;
    this.mimeType = '';
    this.selectedFileName = '';
    this.elapsedSeconds = 0;
    this.result = null;
    this.rawResponse = '';
    this.showRaw = false;
    this.errorMessage = '';
    if (this.audioUrl) URL.revokeObjectURL(this.audioUrl);
    this.audioUrl = '';
    if (resetState) this.state = 'idle';
  }

  scorePercent(value?: number): string {
    if (value === undefined || value === null) return '-';
    return `${Math.round(value * 100)}%`;
  }

  entityEntries(value?: Record<string, unknown>): Array<{ key: string; value: unknown }> {
    if (!value) return [];
    return Object.entries(value).map(([key, entityValue]) => ({ key, value: entityValue }));
  }

  private normalizeExtraction(value: GemmaExtraction): NormalizedGemmaExtraction {
    const entities = { ...(value.entities ?? {}) };
    this.addEntityIfPresent(entities, 'Person name', value.personName);
    this.addEntityIfPresent(entities, 'CNIC', value.cnic);
    this.addEntityIfPresent(entities, 'Phone', value.phone);
    this.addEntityIfPresent(entities, 'Location', value.location);
    this.addEntityIfPresent(entities, 'Department', value.department);

    return {
      ...value,
      sentiment: this.normalizeMetric(value.sentiment),
      intent: this.normalizeMetric(value.intent),
      urgency: this.normalizeMetric(value.urgency),
      recommendedAction: value.recommendedAction || value.action,
      entities,
    };
  }

  private normalizeMetric(value?: GemmaMetric): { label?: string; score?: number; evidence?: string } | undefined {
    if (!value) return undefined;
    if (typeof value === 'string') {
      return { label: this.toTitleCase(value) };
    }
    return {
      ...value,
      label: value.label ? this.toTitleCase(value.label) : value.label,
    };
  }

  private addEntityIfPresent(entities: Record<string, unknown>, key: string, value: unknown): void {
    if (value === undefined || value === null || value === '') return;
    if (entities[key] !== undefined) return;
    entities[key] = value;
  }

  private toTitleCase(value: string): string {
    return value
      .replace(/_/g, ' ')
      .replace(/\w\S*/g, word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
  }

  private finalizeRecording(): void {
    this.stopTimer();
    this.stopTracks();
    this.audioBlob = new Blob(this.chunks, { type: this.mimeType || 'audio/webm' });
    this.fileSize = this.audioBlob.size;
    this.selectedFileName = '';
    if (this.audioUrl) URL.revokeObjectURL(this.audioUrl);
    this.audioUrl = URL.createObjectURL(this.audioBlob);
    this.state = this.audioBlob.size ? 'recorded' : 'failed';
    if (!this.audioBlob.size) this.errorMessage = 'No audio was captured.';
  }

  private pickMimeType(): string {
    return [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/ogg',
    ].find(type => MediaRecorder.isTypeSupported(type)) || '';
  }

  private fileExtension(): string {
    if (this.mimeType.includes('ogg')) return 'ogg';
    if (this.mimeType.includes('wav')) return 'wav';
    if (this.mimeType.includes('mpeg') || this.mimeType.includes('mp3')) return 'mp3';
    return 'webm';
  }

  private get rndApiBase(): string {
    return `${environment.apiHost.replace(/\/$/, '')}/api/v1/rnd`;
  }

  private stopTracks(): void {
    this.mediaStream?.getTracks().forEach(track => track.stop());
    this.mediaStream = null;
  }

  private stopTimer(): void {
    if (!this.timer) return;
    clearInterval(this.timer);
    this.timer = null;
  }
}
